import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { usePipeline } from '../PipelineContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagContext';

const MODEL_BADGE = { fast: '⚡', standard: '◈', smart: '★' };
const MODEL_BADGE_COLOR = { fast: '#64748b', standard: '#6366f1', smart: '#8b5cf6' };
const MODEL_TITLE = {
  fast:     'Efficient — Haiku 4.5 (fast, economical)',
  standard: 'Balanced — Sonnet 4.6 (general purpose)',
  smart:    'Expert — Opus 4.8 (complex reasoning)',
};

const FUNCTION_INFO_AI = {
  what: 'A team of AI agents working on a specific area. The manager coordinates others; all agents share context and tasks.',
  examples: ['Service Desk — triages and resolves support tickets', 'Development — handles bug fixes and feature work'],
};

const FUNCTION_INFO_TASKS = {
  what: 'A team. Holds a task queue your people work through, and the agents that can be given work by a pipeline step. The Playbook here applies to every agent on the team; each agent can add its own instructions on top.',
  examples: ['Dev — a UI, a backend and a security specialist', 'Service desk — agents that triage and answer tickets'],
};

export default function FunctionNode({ id, data, selected }) {
  const [showInfo, setShowInfo] = useState(false);
  const pipeline = usePipeline();
  const { aiEnabled } = useFeatureFlags();
  const { showTokens, tokenData, fmtTokens, fmtCost, openContextMenu, opsMode } = pipeline || {};

  const nodeTokens  = tokenData?.[id];
  const headerStyle = data.color ? { background: data.color + '22', color: data.color } : {};
  const nodeStyle   = data.color ? { borderColor: data.color } : {};
  const taskCount   = (data.tasks || []).length;
  const pendingCount = (data.tasks || []).filter(t => {
    const sf = (data.taskFields || []).find(f => f.name?.toLowerCase() === 'status');
    return sf ? t.values?.[sf.id] !== 'Done' : true;
  }).length;
  const info = aiEnabled ? FUNCTION_INFO_AI : FUNCTION_INFO_TASKS;

  const onAgentCtx = (e, agent) => {
    e.preventDefault();
    e.stopPropagation();
    openContextMenu?.({ type: 'agent', nodeId: id, agent, x: e.clientX, y: e.clientY });
  };

  return (
    <div className={`pipeline-node function-node ${selected ? 'selected' : ''} ${opsMode ? 'ops-dept-node' : ''}`} style={nodeStyle}>
      <Handle type="target" position={Position.Left}  className="node-handle" />

      <div className="node-header function-header" style={headerStyle}>
        <span className="node-type-icon">{opsMode ? '🏢' : '⬡'}</span>
        <span className="node-type-label">{opsMode ? 'Department' : 'Function'}</span>
        <button className="node-info-btn" onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); setShowInfo(s => !s); }}>ⓘ</button>
        {data.agents?.length > 0 && (
          <span className="node-agent-count" style={data.color ? { background: data.color } : {}}>
            {data.agents.length}
          </span>
        )}
        {taskCount > 0 && (
          <span className="node-task-count" style={data.color ? { background: data.color + '22', color: data.color } : {}}>
            {pendingCount > 0 ? `${pendingCount} pending` : `${taskCount} tasks`}
          </span>
        )}
        {showTokens && nodeTokens && aiEnabled && (
          <span className="token-total-badge" style={data.color ? { color: data.color, background: data.color + '18' } : {}}>
            {opsMode ? fmtCost?.(nodeTokens.total) : `${fmtTokens(nodeTokens.total)} tok`}
          </span>
        )}
      </div>

      <div className="node-body">
        <div className="node-title">{data.label}</div>
        {data.description && <div className="node-description">{data.description}</div>}
        {showInfo && (
          <div className="node-info-card">
            <div className="node-info-what">{info.what}</div>
            <div className="node-info-eg-label">Examples:</div>
            {info.examples.map((ex, i) => <div key={i} className="node-info-eg">→ {ex}</div>)}
          </div>
        )}

        {data.agents?.length > 0 ? (
          <div className="node-agents">
            {data.agents.map(agent => (
              <div
                key={agent.id}
                className={`node-agent-chip ${agent.isManager ? 'manager' : ''} ${opsMode ? 'ops-chip' : ''} ${agent.paused ? 'paused' : ''}`}
                onContextMenu={(e) => onAgentCtx(e, agent)}
                title="Right-click for options"
              >
                {opsMode ? (
                  <span className="ops-chip-emoji">{agent.emoji || '🧑'}</span>
                ) : (
                  <span className="agent-chip-dot" style={data.color ? { background: data.color } : {}} />
                )}
                <span className="agent-chip-name">{agent.name}</span>
                {agent.paused && <span className="agent-paused-badge" title={opsMode ? 'On leave' : 'Paused'}>{opsMode ? '🏖️' : '⏸'}</span>}
                {!opsMode && aiEnabled && (
                  <span
                    className="agent-model-badge"
                    style={{ color: MODEL_BADGE_COLOR[agent.model || 'standard'] }}
                    title={MODEL_TITLE[agent.model || 'standard']}
                  >{MODEL_BADGE[agent.model || 'standard']}</span>
                )}
                {agent.isManager && <span className="agent-manager-tag">{opsMode ? 'Head' : 'mgr'}</span>}
                {showTokens && aiEnabled && nodeTokens?.agents?.[agent.id] != null && (
                  <span className="agent-token-count">
                    {opsMode ? fmtCost?.(nodeTokens.agents[agent.id]) : fmtTokens(nodeTokens.agents[agent.id])}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="node-empty-agents">
            {opsMode ? 'No employees assigned' : (aiEnabled ? 'No agents assigned' : 'No team members assigned')}
          </div>
        )}

        {taskCount > 0 && !aiEnabled && (
          <div className="node-task-summary">
            {pendingCount} {opsMode ? 'work items' : 'tasks'} pending
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="node-handle" />
    </div>
  );
}
